import { Play, Square, Timer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { elapsedMs, formatClock, formatLogged } from './clock'
import { useActiveWork, useCardWork, useStartWork, useTick } from './api'
import { StopWorkPopover } from './StopWorkPopover'

/**
 * Start work / Stop work on one item, with the time already logged. It sits in the drawer, where the
 * rest of the item is, so the timer is one click from the thing being timed.
 *
 * What it shows is the server's record: the running clock is the server's start time against the server's
 * clock, so two screens (or two people) never disagree about how long something has been going.
 */
export function WorkTimer({ cardId, completed }: { cardId: string; completed: boolean }) {
  const summary = useCardWork(cardId)
  const active = useActiveWork()
  const start = useStartWork()
  const mine = active.data?.session && active.data.session.cardId === cardId ? active.data : null
  const running = Boolean(mine?.session && !mine.session.endedAt)
  const now = useTick(running)

  if (summary.isPending || !summary.data) return null
  const data = summary.data
  const clock = mine?.session ? elapsedMs(mine.session.startedAt, mine.now, mine.fetchedAt, now) : 0
  const drift = running ? Math.max(0, now - data.fetchedAt) : 0
  const logged = data.totalMs + drift
  const elsewhere = active.data?.session && active.data.session.cardId !== cardId ? active.data.card : null
  const canTime = data.canTime !== false && !completed
  const others = data.byUser.filter((u) => u.running)

  return (
    <div className="rounded-lg bg-surface-sunken p-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="microlabel flex items-center gap-1.5">
          <Timer className="size-3" />
          Time on this item
        </p>
        <span className="num text-sm font-semibold">{formatLogged(logged)}</span>
        {others.length > 0 ? (
          <span className="flex items-center gap-1.5 text-tiny text-text-muted">
            <span className="size-2 rounded-full bg-ios-green" aria-hidden />
            {others.map((u) => u.displayName).join(', ')} working now
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {running ? (
            <>
              <span className="num text-lg font-semibold tabular-nums" role="timer" aria-label="Time on this session">
                {formatClock(clock)}
              </span>
              <StopWorkPopover>
                <Button type="button" size="sm" variant="destructive">
                  <Square className="size-3.5" fill="currentColor" /> Stop work
                </Button>
              </StopWorkPopover>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={!canTime || start.isPending}
              title={
                completed
                  ? 'Completed work cannot be timed.'
                  : !canTime
                    ? 'Only the owner or an assignee can time this item.'
                    : elsewhere
                      ? `Stops your timer on ${elsewhere.code ?? elsewhere.title}.`
                      : undefined
              }
              onClick={() => start.mutate(cardId)}
            >
              <Play className="size-3.5" fill="currentColor" /> Start work
            </Button>
          )}
        </div>
      </div>
      {data.sessions.length > 0 ? (
        <details className="mt-2 text-tiny text-text-muted" open={data.sessions.length <= 4}>
          <summary className="cursor-pointer select-none hover:text-text-secondary">Work log · {data.sessions.length}</summary>
          <ol className="mt-1.5 grid gap-1.5">
            {data.sessions.map((s) => {
              const who = data.byUser.find((u) => u.userId === s.userId)?.displayName ?? 'Someone'
              const ms = Math.max(0, (s.endedAt ? Date.parse(s.endedAt) : data.fetchedAt + (now - data.fetchedAt)) - Date.parse(s.startedAt))
              return (
                <li key={s.id} className="rounded-md bg-card px-2 py-1.5">
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-text-secondary">{who}</span>
                    <span>{new Date(s.startedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>
                    <span className="num ml-auto font-semibold text-foreground">{s.endedAt ? formatLogged(ms) : 'running'}</span>
                  </p>
                  {s.note ? <p className="mt-0.5 text-text-secondary">{s.note}</p> : null}
                </li>
              )
            })}
          </ol>
        </details>
      ) : null}
      {data.byUser.length > 0 ? (
        <details className="mt-2 text-tiny text-text-muted">
          <summary className="cursor-pointer select-none hover:text-text-secondary">Who has spent time · {data.byUser.length}</summary>
          <ul className="mt-1 grid gap-1">
            {data.byUser.map((u) => (
              <li key={u.userId} className="flex items-center justify-between gap-3">
                <span className={cn('truncate', u.running && 'font-medium text-text-secondary')}>{u.displayName}{u.running ? ' · running' : ''}</span>
                <span className="num">{formatLogged(u.ms + (u.running && mine ? drift : 0))}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}
